'use client';

import { ICartGroup } from '@/api/types/cart';
import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useTranslations } from '@/i18n/use-translations';
import { forwardRef, useContext, useState } from 'react';

interface RedeemedConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  exchangeCouponList: ICartGroup[];
  onConfirm: () => Promise<void>;
}

const RedeemedConfirmDialog = forwardRef<HTMLDivElement, RedeemedConfirmDialogProps>(
  ({ open, onClose, exchangeCouponList, onConfirm }, ref) => {
    const t = useTranslations('cart.redeemedConfirmDialog');
    const { deviceType } = useContext(ResponsiveContext);
    const isPc = deviceType === EPlatform.WEB;
    const [loading, setLoading] = useState(false);

    // 获取兑换券信息
    const firstCoupon = exchangeCouponList?.[0];
    const couponNumber = (firstCoupon as any)?.number || 1;
    const thresholdQuantity = parseInt((firstCoupon as any)?.threshold_quantity || '0');

    // 根据平台设置弹窗宽度
    const dialogWidth = isPc ? '410px' : '320px';

    const handleConfirm = async () => {
      if (loading) return;

      try {
        setLoading(true);
        await onConfirm?.();
        onClose();
      } catch (error) {
        logger.error('兑换失败:', error);
      } finally {
        setLoading(false);
      }
    };

    return (
      <Dialog
        ref={ref}
        open={open}
        title=""
        hideHeader
        footer={false}
        disableEscapeKeyDown={loading}
        sx={{
          '& .MuiDialog-paper': {
            width: dialogWidth,
            maxWidth: dialogWidth,
          },
          '& .MuiDialogContent-root': {
            padding: '47px 22px 24px',
          },
        }}
      >
        <div className="text-left">
          {/* 标题 */}
          <h3 className="text-[14px] md:text-[16px] font-bs text-primary-black text-center mb-[20px]">
            {t('redeemItems')}
          </h3>

          {/* 内容区域 - 左对齐 */}
          <div className="space-y-[8px] text-left">
            {/* 可用券数信息 */}
            <p className="text-[12px] md:text-[16px] text-primary-black leading-[1.4]">
              {couponNumber > 1 ? t('currentCouponsPlural') : t('currentCoupons')} {t('available')}{' '}
              <span className="font-semibold text-[#463ae8]">{couponNumber}</span>
            </p>

            {/* 确认信息 */}
            <p className="text-[12px] md:text-[14px] text-primary-black leading-[1.4]">
              {t('willUse')}
            </p>

            {/* 数量限制提示 */}
            {thresholdQuantity > 0 && (
              <p className="text-[12px] md:text-[14px] leading-[1.4] mt-[12px]">
                {t('addMore')} <span className="font-semibold">{thresholdQuantity}</span>{' '}
                {t('moreItems')}
              </p>
            )}
          </div>

          {/* 按钮组 */}
          <div className="flex gap-[15px] justify-center mt-[30px]">
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              className="flex-1 !w-[130px] !h-[40px] !rounded-[50px] md:!rounded-[2px] !text-[16px] !font-bb"
            >
              {t('no')}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              loading={loading}
              className="flex-1 !w-[130px] !h-[40px] !rounded-[50px] md:!rounded-[2px] !text-[16px] !font-bb"
            >
              {t('yes')}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  },
);

RedeemedConfirmDialog.displayName = 'RedeemedConfirmDialog';

export default RedeemedConfirmDialog;
